// Firestore and Storage paths. Use these instead of string literals so a rename happens in one place.

// The fixed document ids come from the modules that own the rest of those documents' contracts.
// The edge runs one way only: appConfig.ts and live.ts must never import this file.
import { APP_CONFIG_DOC } from './appConfig.ts'
import { LIVE_SUMMARY_DOC } from './live.ts'

export const DEFAULT_EVENT_ID = 'midwest-2026'

export const TOP = {
  platformAdmins: 'platformAdmins',
  profileLinks: 'profileLinks',
  /** Function-only index of the current coach link per team (never client-readable). */
  profileLinkIndex: 'profileLinkIndex',
  mail: 'mail',
  mailQuota: 'mailQuota',
  /** Function-only: per-event random salts for public ids derived from private data. */
  importSalts: 'importSalts',
  events: 'events',
  /** The native app's public control plane: one get-only document (NATIVE-APPS-PLAN.md §6). */
  appConfig: 'appConfig',
} as const

/** Subcollections under events/{eventId}. */
export const SUB = {
  staff: 'staff',
  content: 'content',
  divisions: 'divisions',
  teams: 'teams',
  teamsPrivate: 'teamsPrivate',
  teamProfileSubmissions: 'teamProfileSubmissions',
  payments: 'payments',
  games: 'games',
  gameLive: 'gameLive',
  gameDrafts: 'gameDrafts',
  schedulePublishes: 'schedulePublishes',
  scheduleChanges: 'scheduleChanges',
  courtStatus: 'courtStatus',
  /** The attendee app's single realtime document, written only by Cloud Functions (D33). */
  liveSummary: 'liveSummary',
  courtKeepers: 'courtKeepers',
  alerts: 'alerts',
  results: 'results',
  announcements: 'announcements',
  feed: 'feed',
  alertHistory: 'alertHistory',
  devices: 'devices',
  vendors: 'vendors',
  vendorsPrivate: 'vendorsPrivate',
  sponsors: 'sponsors',
  sponsorsPrivate: 'sponsorsPrivate',
  config: 'config',
  quranEntrants: 'quranEntrants',
  quranOrder: 'quranOrder',
  quranLive: 'quranLive',
  quranResults: 'quranResults',
  checkins: 'checkins',
  emailCampaigns: 'emailCampaigns',
  imports: 'imports',
  auditLog: 'auditLog',
  /** Staff-only team withdrawal records (Lane E), one per team: written by teamWithdraw / teamReinstate. */
  withdrawals: 'withdrawals',
  /**
   * Function-only idempotency markers for the Premium promotion push (D158), one per promotion
   * identity (`promotionPushId`: vendor and window start). Default-deny in the rules: no client reads
   * or writes them.
   */
  promotionPushes: 'promotionPushes',
} as const

/** Score events live under gameLive/{gameId}/events. */
export const LIVE_EVENTS = 'events'

export type EventSub = (typeof SUB)[keyof typeof SUB]

export const CONFIG_DOCS = {
  bazaarLayout: 'bazaarLayout',
  bazaarPromotions: 'bazaarPromotions',
  sponsorTiers: 'sponsorTiers',
  vendorCategories: 'vendorCategories',
} as const

/**
 * The pointer. This file's only zero-argument helper, because there is exactly one such document
 * and it is the one thing here that is not event-scoped — it is what NAMES the event (§6 item 1).
 */
export const appConfigDoc = () => `${TOP.appConfig}/${APP_CONFIG_DOC}`

export const eventDoc = (eventId: string) => `${TOP.events}/${eventId}`
export const eventCol = (eventId: string, sub: EventSub) => `${TOP.events}/${eventId}/${sub}`
export const eventItem = (eventId: string, sub: EventSub, id: string) => `${TOP.events}/${eventId}/${sub}/${id}`
export const scoreEventsCol = (eventId: string, gameId: string) => `${eventItem(eventId, SUB.gameLive, gameId)}/${LIVE_EVENTS}`
export const liveSummaryDoc = (eventId: string) => eventItem(eventId, SUB.liveSummary, LIVE_SUMMARY_DOC)
export const staffDoc = (eventId: string, uid: string) => eventItem(eventId, SUB.staff, uid)
export const paymentId = (kind: 'team' | 'vendor' | 'sponsor', refId: string) => `${kind}_${refId}`
export const configDoc = (eventId: string, name: keyof typeof CONFIG_DOCS) => eventItem(eventId, SUB.config, CONFIG_DOCS[name])
export const checkinId = (kind: 'team' | 'vendor', refId: string) => `${kind}_${refId}`

/** Storage paths. */
export const STORAGE = {
  /** The coach's photo before review. One fixed object per link; written by the token holder only. */
  coachUpload: (token: string) => `coachUploads/${token}/photo`,
  /** Public images the app shows. Written by staff or functions. */
  teamPhoto: (eventId: string, teamId: string, fileName: string) => `public/events/${eventId}/teams/${teamId}/${fileName}`,
  vendorLogo: (eventId: string, vendorId: string, fileName: string) => `public/events/${eventId}/vendors/${vendorId}/${fileName}`,
  sponsorLogo: (eventId: string, sponsorId: string, fileName: string) => `public/events/${eventId}/sponsors/${sponsorId}/${fileName}`,
  /** Staff-only copy of a coach's submitted photo for review (teams.manage). */
  reviewPhoto: (eventId: string, teamId: string) => `review/events/${eventId}/teams/${teamId}/photo.jpg`,
} as const
